import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { errMsg, useApi } from "@/lib/api";
import ClientDialog from "./ClientDialog";

const slugify = (s) => s.normalize("NFKD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 50);
const EMPTY = { client_id: "", project_id: "", name: "", slug: "", site_url: "", auto_preview: false };

function Field({ label, hint, children }) {
  return <div><Label className="text-xs">{label}</Label><div className="mt-1">{children}</div>{hint && <p className="mt-1 text-[11px] text-slate-500">{hint}</p>}</div>;
}

export default function NewSiteDialog({ open, onOpenChange, defaultClient }) {
  const api = useApi();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState(null);
  const [f, setF] = useState(EMPTY);
  const [slugTouched, setSlugTouched] = useState(false);
  const [buildNow, setBuildNow] = useState(true);
  const [newClient, setNewClient] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setF({ ...EMPTY, client_id: defaultClient || "" }); setSlugTouched(false); setBuildNow(true);
    api.get("/clients").then(({ data }) => setClients(data)).catch(() => {});
    api.get("/sites/available-projects").then(({ data }) => setProjects(data)).catch(() => setProjects([]));
  }, [open, api, defaultClient]);
  const setName = (name) => setF((x) => ({ ...x, name, slug: slugTouched ? x.slug : slugify(name) }));
  const pickProject = (id) => {
    const p = projects.find((x) => x.id === id);
    setF((x) => ({ ...x, project_id: id }));
    if (p && !f.name) setName(p.company_name || p.name);
  };
  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/sites", f);
      if (buildNow) await api.post(`/sites/${data.id}/deploys`, { set_preview: true });
      toast.success(buildNow ? `Sito «${data.name}» creato: compilazione in coda` : `Sito «${data.name}» creato`);
      onOpenChange(false);
      navigate(`/sites/${data.id}`);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const valid = f.client_id && f.project_id && f.name.trim().length >= 2 && f.slug;
  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-xl" data-testid="new-site-dialog">
          <DialogHeader>
            <DialogTitle>Nuovo sito</DialogTitle>
            <DialogDescription>Il sito viene compilato dal contenuto del progetto: il progetto e la sua demo non vengono mai modificati.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Cliente *">
              <div className="flex gap-2">
                <Select value={f.client_id} onValueChange={(v) => setF({ ...f, client_id: v })}>
                  <SelectTrigger data-testid="new-site-client-select"><SelectValue placeholder="Scegli il cliente" /></SelectTrigger>
                  <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id} data-testid={`new-site-client-${c.id}`}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
                <Button variant="outline" size="icon" onClick={() => setNewClient(true)} aria-label="Nuovo cliente" data-testid="new-site-add-client-btn"><Plus size={15} /></Button>
              </div>
            </Field>
            <Field label="Progetto *" hint={projects?.length === 0 ? "Tutti i progetti alimentano già un sito." : "Un progetto alimenta un solo sito."}>
              <Select value={f.project_id} onValueChange={pickProject}>
                <SelectTrigger data-testid="new-site-project-select"><SelectValue placeholder={projects ? "Scegli il progetto" : "Caricamento…"} /></SelectTrigger>
                <SelectContent>{(projects || []).map((p) => <SelectItem key={p.id} value={p.id} data-testid={`new-site-project-${p.id}`}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Nome del sito *"><Input value={f.name} onChange={(e) => setName(e.target.value)} data-testid="new-site-name-input" /></Field>
            <Field label="Identificativo *" hint="Usato negli indirizzi di anteprima e, con Cloudflare, come sottodominio.">
              <Input value={f.slug} onChange={(e) => { setSlugTouched(true); setF({ ...f, slug: slugify(e.target.value) }); }} data-testid="new-site-slug-input" />
            </Field>
            <div className="sm:col-span-2">
              <Field label="Indirizzo definitivo (facoltativo)" hint="Es. https://www.cliente.it: serve per sitemap e canonical. Il collegamento del dominio del cliente non è ancora disponibile.">
                <Input value={f.site_url} placeholder="https://www.cliente.it" onChange={(e) => setF({ ...f, site_url: e.target.value })} data-testid="new-site-url-input" />
              </Field>
            </div>
            <label className="sm:col-span-2 flex items-start gap-3 rounded-lg border border-slate-200 p-3">
              <Switch checked={f.auto_preview} onCheckedChange={(v) => setF({ ...f, auto_preview: v })} data-testid="new-site-auto-preview-switch" />
              <span className="text-sm text-slate-700">Aggiorna l'anteprima a ogni salvataggio di versione<span className="block text-xs text-slate-500">Quando salvi una versione del progetto parte da sola una compilazione; la pubblicazione online resta sempre manuale.</span></span>
            </label>
            <label className="sm:col-span-2 flex items-center gap-2 text-sm text-slate-700">
              <Checkbox checked={buildNow} onCheckedChange={(v) => setBuildNow(!!v)} data-testid="new-site-build-now-checkbox" />Compila subito e imposta l'anteprima
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="new-site-cancel-btn">Annulla</Button>
            <Button onClick={create} disabled={!valid || busy} className="bg-sky-600 hover:bg-sky-700" data-testid="new-site-create-btn">
              {busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}Crea sito
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ClientDialog open={newClient} onOpenChange={setNewClient} onSaved={(c) => { setClients((l) => [...l, c]); setF((x) => ({ ...x, client_id: c.id })); }} />
    </>
  );
}
